import { useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

const CROP_SIZE = 260;
const OUTPUT_SIZE = 512;

type Offset = { x: number; y: number };
type LoadedImage = {
  url: string;
  width: number;
  height: number;
  baseScale: number;
};

type Props = {
  file: File;
  onCancel: () => void;
  onSave: (photo: string) => Promise<void>;
};

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

function constrainOffset(offset: Offset, image: LoadedImage, zoom: number): Offset {
  const horizontal = Math.max(0, (image.width * image.baseScale * zoom - CROP_SIZE) / 2);
  const vertical = Math.max(0, (image.height * image.baseScale * zoom - CROP_SIZE) / 2);
  return {
    x: clamp(offset.x, -horizontal, horizontal),
    y: clamp(offset.y, -vertical, vertical),
  };
}

export function ProfilePhotoCropper({ file, onCancel, onSave }: Props) {
  const [image, setImage] = useState<LoadedImage>();
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const imageElement = useRef<HTMLImageElement>(null);
  const drag = useRef<{ pointerId: number; x: number; y: number; offset: Offset } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const probe = new Image();
    let active = true;
    setImage(undefined);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setError("");
    probe.onload = () => {
      if (!active) return;
      setImage({
        url,
        width: probe.naturalWidth,
        height: probe.naturalHeight,
        baseScale: Math.max(CROP_SIZE / probe.naturalWidth, CROP_SIZE / probe.naturalHeight),
      });
    };
    probe.onerror = () => {
      if (active) setError("Não foi possível abrir esta fotografia. Escolha uma imagem JPG, PNG ou WebP.");
    };
    probe.src = url;
    return () => {
      active = false;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const previewSize = useMemo(() => image ? {
    width: image.width * image.baseScale,
    height: image.height * image.baseScale,
  } : undefined, [image]);

  function startDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (!image || busy) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, offset };
  }

  function moveDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (!image || !drag.current || drag.current.pointerId !== event.pointerId) return;
    setOffset(constrainOffset({
      x: drag.current.offset.x + event.clientX - drag.current.x,
      y: drag.current.offset.y + event.clientY - drag.current.y,
    }, image, zoom));
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (drag.current?.pointerId === event.pointerId) drag.current = null;
  }

  function updateZoom(nextZoom: number) {
    if (!image) return;
    const constrainedZoom = clamp(nextZoom, 1, 3);
    setZoom(constrainedZoom);
    setOffset((current) => constrainOffset(current, image, constrainedZoom));
  }

  async function saveCrop() {
    if (!image || !imageElement.current) return;
    setBusy(true);
    setError("");
    try {
      const displayScale = image.baseScale * zoom;
      const sourceSize = CROP_SIZE / displayScale;
      const sourceX = (image.width - sourceSize) / 2 - offset.x / displayScale;
      const sourceY = (image.height - sourceSize) / 2 - offset.y / displayScale;
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("CANVAS_UNAVAILABLE");
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(imageElement.current, sourceX, sourceY, sourceSize, sourceSize, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
      await onSave(canvas.toDataURL("image/webp", 0.88));
    } catch {
      setError("Não foi possível guardar a fotografia. Tente novamente.");
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop profile-photo-crop-backdrop" onClick={() => { if (!busy) onCancel(); }}>
      <section className="sheet profile-photo-crop-sheet" role="dialog" aria-modal="true" aria-labelledby="profile-photo-crop-title" onClick={(event) => event.stopPropagation()}>
        <div className="profile-photo-crop-header">
          <button type="button" onClick={onCancel} disabled={busy} aria-label="Cancelar">×</button>
          <div>
            <h2 id="profile-photo-crop-title">Ajustar fotografia</h2>
            <p>Arraste a imagem para escolher o enquadramento.</p>
          </div>
          <span />
        </div>

        <div className="profile-photo-crop-stage" onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}>
          {image && previewSize ? <img
            ref={imageElement}
            src={image.url}
            alt="Pré-visualização da fotografia de perfil"
            draggable={false}
            style={{
              width: previewSize.width,
              height: previewSize.height,
              transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            }}
          /> : !error && <span className="profile-photo-crop-loading" aria-label="A carregar" />}
        </div>

        <label className="profile-photo-zoom">
          <button type="button" onClick={() => updateZoom(zoom - 0.1)} disabled={!image || busy || zoom <= 1} aria-label="Reduzir fotografia">−</button>
          <input type="range" min="1" max="3" step="0.01" value={zoom} disabled={!image || busy} aria-label="Ampliação da fotografia" onChange={(event) => {
            updateZoom(Number(event.target.value));
          }} />
          <button type="button" onClick={() => updateZoom(zoom + 0.1)} disabled={!image || busy || zoom >= 3} aria-label="Ampliar fotografia">+</button>
        </label>

        {error && <p className="profile-photo-crop-error" role="alert">{error}</p>}
        <div className="profile-photo-crop-actions">
          <button type="button" className="secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button type="button" className="primary" onClick={saveCrop} disabled={!image || busy}>{busy ? "A guardar…" : "Guardar fotografia"}</button>
        </div>
      </section>
    </div>
  );
}
